// ============================================================
// liveCaseRefresh.ts — (D12 live updates) re-read the STORE-backed parts of
// the open case after someone else changed them: the case row (header, hero,
// every field section, case control, linked cases), its witnesses,
// deadlines, documents (→ the sidebar counts) and the TPI directory.
// Component-backed parts (settlement, billing, activity…) re-read on the
// change bus themselves (utils/caseChangeEvents).
//
// ★ NEVER over unsaved work. A re-read waits while this user has:
//   • a case edit not yet saved (typed / staged / in flight — useAutoSave),
//   • a witness / deadline edit not yet saved,
//   • a field focused (they're typing in it),
//   • a card open for editing (a "hold" — SectionCards / explicit-Save cards
//     register one while open; they show "Updated by … — Reload" meanwhile).
// It retries every RETRY_MS, and at once when the last hold is released.
// Per-field changes keep arriving meanwhile on the existing field-edit relay.
// The re-read adopts the fresh row as the saved baseline (so the autosave
// doesn't send the other person's values back), and the concurrency base
// moves to the server's updatedAt — the user's next Save is still checked
// against it (409 handling unchanged).
// ============================================================
import { useEffect, useRef } from 'react'
import { useCasevoStore } from '../store/useCasevoStore'
import { fetchCaseById, listFilesApi, listWitnessesApi, listCaseDeadlinesApi, listTpiContactsApi } from '../api/casevoApi'
import { presence } from '../services/presence'
import { adoptOpenCaseAsSaved, hasUnsavedCaseEdit } from './useAutoSave'
import { hasPendingWitnessSaves } from './useWitnessAutoSave'
import { hasPendingDeadlineSaves } from './useCaseDeadlineAutoSave'
import { setWitnessBaseUpdatedAt, setWitnessBaseValues, setDeadlineBaseUpdatedAt, setDeadlineBaseValues } from './saveBaseVersion'

export const RETRY_MS = 2000

// ── Holds (a card open for editing on the open case) ──
const holds = new Map<symbol, string>()
export const isCaseRefreshHeld = (caseId: string): boolean => {
  for (const id of Array.from(holds.values())) if (id === caseId) return true
  return false
}
export const setCaseRefreshHold = (token: symbol, caseId: string | null): void => {
  const had = holds.get(token)
  if (caseId) holds.set(token, caseId)
  else holds.delete(token)
  if (had && !caseId && !isCaseRefreshHeld(had)) kick()
}
// Hold the open case's re-read while `active` (released on unmount).
export const useCaseRefreshHold = (caseId: string | null | undefined, active: boolean): void => {
  const token = useRef(Symbol('case-refresh-hold')).current
  useEffect(() => {
    setCaseRefreshHold(token, active && caseId ? caseId : null)
    return () => setCaseRefreshHold(token, null)
  }, [token, caseId, active])
}

// ── What's waiting ──
type Part = 'case' | 'files' | 'witnesses' | 'deadlines' | 'tpi'
const waiting = new Map<string, Set<Part>>()
let retryTimer: ReturnType<typeof setTimeout> | null = null

const isOpen = (caseId: string): boolean => useCasevoStore.getState().currentCase?.id === caseId
const caseBlocked = (caseId: string): boolean =>
  hasUnsavedCaseEdit() || hasPendingWitnessSaves() || hasPendingDeadlineSaves() || !!presence.getLocalFocusedField() || isCaseRefreshHeld(caseId)

const defer = (caseId: string, part: Part): void => {
  const s = waiting.get(caseId) ?? new Set<Part>()
  s.add(part)
  waiting.set(caseId, s)
  if (!retryTimer) retryTimer = setTimeout(() => { retryTimer = null; kick() }, RETRY_MS)
}

// Run whatever is waiting (for a case that's still open).
const kick = (): void => {
  const entries = Array.from(waiting.entries())
  waiting.clear()
  for (const [caseId, parts] of entries) {
    if (!isOpen(caseId)) continue
    if (parts.has('case')) { void refreshOpenCase(caseId); continue } // the case re-read brings every part
    if (parts.has('files')) void refreshOpenCaseFiles(caseId)
    if (parts.has('witnesses')) void refreshOpenCaseWitnesses(caseId)
    if (parts.has('deadlines')) void refreshOpenCaseDeadlines(caseId)
    if (parts.has('tpi')) void refreshTpiContacts()
  }
}

// The whole case (row + witnesses + deadlines + files + TPI) — the same load
// as opening it, landing as a refresh of the case already open.
export const refreshOpenCase = async (caseId: string): Promise<void> => {
  if (!isOpen(caseId)) return
  if (caseBlocked(caseId)) { defer(caseId, 'case'); return }
  let raw
  try { raw = await fetchCaseById(caseId) } catch { return } // a 403/404 also arrives as entity-changed; keep what's shown
  if (!isOpen(caseId)) return
  if (caseBlocked(caseId)) { defer(caseId, 'case'); return } // they started typing meanwhile
  try {
    await useCasevoStore.getState().setCurrentCaseById(caseId, raw, () => adoptOpenCaseAsSaved())
  } catch { /* keep what's shown */ }
}

// Documents (the Documents section + the sidebar count). Nothing is edited
// in place, so no wait.
export const refreshOpenCaseFiles = async (caseId: string): Promise<void> => {
  const st = useCasevoStore.getState()
  if (st.files[caseId] === undefined && !isOpen(caseId)) return
  try {
    const files = await listFilesApi(caseId)
    useCasevoStore.getState().setFiles(caseId, files)
  } catch { /* keep what's shown */ }
}

export const refreshOpenCaseWitnesses = async (caseId: string): Promise<void> => {
  if (!useCasevoStore.getState().witnesses[caseId]) return
  if (hasPendingWitnessSaves() || !!presence.getLocalFocusedField()) { defer(caseId, 'witnesses'); return }
  try {
    const list = await listWitnessesApi(caseId)
    if (hasPendingWitnessSaves() || !!presence.getLocalFocusedField()) { defer(caseId, 'witnesses'); return }
    list.forEach((w) => { setWitnessBaseUpdatedAt(w.id, w.updatedAt); setWitnessBaseValues(w.id, w as unknown as Record<string, unknown>) })
    useCasevoStore.getState().setWitnesses(caseId, list)
  } catch { /* keep what's shown */ }
}

export const refreshOpenCaseDeadlines = async (caseId: string): Promise<void> => {
  if (!useCasevoStore.getState().caseDeadlines[caseId]) return
  if (hasPendingDeadlineSaves() || !!presence.getLocalFocusedField()) { defer(caseId, 'deadlines'); return }
  try {
    const list = await listCaseDeadlinesApi(caseId)
    if (hasPendingDeadlineSaves() || !!presence.getLocalFocusedField()) { defer(caseId, 'deadlines'); return }
    list.forEach((d) => { setDeadlineBaseUpdatedAt(d.id, d.updatedAt); setDeadlineBaseValues(d.id, d as unknown as Record<string, unknown>) })
    useCasevoStore.getState().setCaseDeadlines(caseId, list)
  } catch { /* keep what's shown */ }
}

export const refreshTpiContacts = async (): Promise<void> => {
  try { useCasevoStore.getState().setTpiContacts(await listTpiContactsApi()) } catch { /* keep what's shown */ }
}

// Server topics → what to re-read for the case (only when it's the open one;
// documents also for a case whose files are loaded).
const CASE_ROW_TOPICS = new Set(['case', 'client', 'parties', 'accident', 'medical', 'funding'])
export const refreshCaseStoreForTopics = (caseId: string, topics: readonly string[]): void => {
  const t = new Set(topics)
  // Documents straight away (nothing is edited in place) — even when the
  // case re-read below has to wait for the user's typing.
  if (t.has('documents') || t.has('esign')) void refreshOpenCaseFiles(caseId)
  if (!isOpen(caseId)) return
  if (topics.some((x) => CASE_ROW_TOPICS.has(x))) { void refreshOpenCase(caseId); return }
  if (t.has('witnesses')) void refreshOpenCaseWitnesses(caseId)
  if (t.has('deadlines')) void refreshOpenCaseDeadlines(caseId)
}

// Tests only.
export const _resetLiveCaseRefresh = (): void => {
  holds.clear(); waiting.clear()
  if (retryTimer) { clearTimeout(retryTimer); retryTimer = null }
}
